

import Vue from 'vue';
import moment from 'moment';
import Decimal from 'decimal.js';

/**
 * 格式化数字，实现四舍五入并保留指定小数位数。
 *
 * @param {number} f - 需要格式化的数字。
 * @param {number} [digit=2] - 保留的小数位数，默认为 2 位。
 * @returns {number} - 格式化后的数字。
 */
const FormatNum = function (f, digit = 2) {
    // 计算 10 的 digit 次幂，用于后续的小数位处理
    let m = Math.pow(10, digit);
    // 将原数字乘以 10 的 digit 次幂，再加上 0.5 以实现四舍五入
    let des = f * m + 0.5;
    // 将结果转换为整数，去掉小数部分，再除以 10 的 digit 次幂还原小数位
    return parseInt(des, 10) / m;
};


/**
 * 将数字按照指定的小数位数进行格式化。
 * 使用 Decimal 库确保在处理浮点数时能得到精确的结果。
 *
 * @param {number|string} num - 需要格式化的数字，可以是数字类型或可转换为数字的字符串。
 * @param {number} s - 保留的小数位数。
 * @returns {string} - 格式化后的字符串，包含指定小数位数。
 */
const toFixed = function (num, s) {
    // 使用 Decimal 库创建一个 Decimal 对象，传入需要格式化的数字
    // 然后调用 toFixed 方法，按照指定的小数位数进行格式化
    return Decimal(num).toFixed(s);
};


/**
 * 自定义 Vue 过滤器 dxMoney，用于将数字金额转换为中文大写金额。
 * 若输入的数字不符合要求或为 0，则返回空字符串。
 *
 * @param {string|number} n - 需要转换为中文大写的数字金额。
 * @returns {string} - 转换后的中文大写金额字符串，若不符合条件则返回空字符串。
 */
Vue.filter("dxMoney", (n) => {
    // 检查输入的 n 是否为有效的数字且不为 0，若不满足条件则返回空字符串
    if (!/^(0|[1-9]\d*)(\.\d+)?$/.test(n) || n == 0) {
        return "";
    }

    // 定义中文大写金额的单位
    let unit = "仟佰拾亿仟佰拾万仟佰拾元角分";
    // 初始化存储中文大写金额的字符串
    let str = "";
    // 在数字后面添加两个 0，用于处理小数部分
    n += "00";
    // 查找小数点的位置
    let p = n.indexOf('.');
    if (p >= 0) {
        // 若存在小数点，去掉小数点并截取前两位小数
        n = n.substring(0, p) + n.substr(p + 1, 2);
    }
    // 截取与数字长度对应的单位部分
    unit = unit.substr(unit.length - n.length);
    // 遍历数字的每一位，将其转换为中文大写并添加对应的单位
    for (let i = 0; i < n.length; i++) {
        str += '零壹贰叁肆伍陆柒捌玖'.charAt(n.charAt(i)) + unit.charAt(i);
    }

    // 对生成的中文大写金额字符串进行一系列的格式优化
    return str.replace(/零(仟|佰|拾|角)/g, "零").replace(/(零)+/g, "零").replace(/零(万|亿|元)/g, "$1").replace(/(亿)万|壹(拾)/g, "$1$2").replace(/^元零?|零分/g, "").replace(/元$/g, "元整");
});


/**
 * 自定义 Vue 过滤器 fqFormat，用于将日期格式化为 "YYYY 年第 MM 期" 的字符串。
 * 若输入值无效或为空，则返回空字符串。
 *
 * @param {any} n - 要格式化的日期，可以是任何能被 moment 解析的值。
 * @returns {string} - 格式化后的日期字符串，若输入无效则返回空字符串。
 */
Vue.filter("fqFormat", (n) => {
    // 检查输入的 n 是否为无效值（如 null、undefined、空字符串等），若是则返回空字符串
    if (!n) {
        return ''
    }
    // 使用 moment 库将输入的日期进行解析，并格式化为 "YYYY 年第 MM 期" 的字符串
    return moment(n).format("YYYY年第MM期");
});


/**
 * 自定义 Vue 过滤器 moneyFormat，用于将数字金额按不同量级进行格式化显示。
 * 若输入值无效，则默认显示 '0'。
 *
 * @param {number} n - 需要格式化的数字金额。
 * @returns {string|number} - 格式化后的金额字符串或原始数字，根据不同量级显示不同单位。
 */
Vue.filter("moneyFormat", (n) => {
    // 检查输入的 n 是否为无效值（如 null、undefined、0 等），若是则返回 '0'
    if (!n) {
        return '0'
    }
    // 获取输入数字的绝对值
    let num = Math.abs(n);
    // 若绝对值小于 10000，直接返回原始数字
    if (num < 10000) {
        return n;
    // 若绝对值大于等于 10000 且小于 1 亿，将数字除以 10000 并调用 FormatNum 函数进行四舍五入格式化，添加 '万' 单位
    } else if (num >= 10000 && num < 100000000) {
        return FormatNum(n / 10000) + "万";
    // 若绝对值大于等于 1 亿，将数字除以 1 亿并调用 FormatNum 函数进行四舍五入格式化，保留 4 位小数，添加 '亿' 单位
    } else {
        return FormatNum(n / 100000000, 4) + "亿";
    }
});


/**
 * 自定义 Vue 过滤器 numFormat，用于将数字格式化为带千分位分隔符的字符串，并保留指定小数位数。
 * 若输入无效，返回空字符串。
 *
 * @param {number|string} number - 需要格式化的数字。
 * @returns {string} - 格式化后的字符串。
 */
Vue.filter("numFormat", (number) => {
	if (!number) {
		return ''
	}
	/*
　　 * 参数说明：
　　 * number：要格式化的数字
　　 * decimals：保留几位小数
　　 * dec_point：小数点符号
　　 * thousands_sep：千分位符号
　　 * */
	number = (number + '').replace(/[^0-9+-Ee.]/g, '');
	let n = !isFinite(+number) ? 0 : +number, prec = 2, sep = ',', dec = '.', s = '';

    // 根据 prec 是否有值，决定是使用 toFixed 方法保留指定小数位数，
    // 还是直接对数字进行四舍五入取整，然后将结果按小数点分割成整数和小数两部分
    s = (prec ? toFixed(n, prec) : '' + Math.round(n)).split('.');

    // 定义正则表达式，用于匹配整数部分中从右往左每三位一组的数字
    let re = /(-?\d+)(\d{3})/;
    // 循环检查整数部分是否能匹配到符合条件的数字组
    while (re.test(s[0])) {
        // 若匹配成功，在每三位数字前插入千分位分隔符
        s[0] = s[0].replace(re, "$1" + sep + "$2");
    }
    // 检查小数部分的长度是否小于指定的小数位数
    if ((s[1] || '').length < prec) {
        // 若小数部分为空，将其初始化为空字符串
        s[1] = s[1] || '';
        // 在小数部分末尾补零，使其长度达到指定的小数位数
        s[1] += new Array(prec - s[1].length + 1).join('0');
    }
    // 将整数部分和小数部分用小数点连接起来并返回
    return s.join(dec);
});
